import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createE2EEEvent } from '../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../analytics/functions';
import { setStartMutedPolicy } from '../../base/conference/actions';
import Switch from '../../base/ui/components/web/Switch';
import { showMessageNotification, showWarningNotification } from '../../notifications/actions';
import { NOTIFICATION_ICON, NOTIFICATION_TIMEOUT_TYPE } from '../../notifications/constants';
import { openSettingsDialog } from '../../settings/actions.web';
import { SETTINGS_TABS } from '../../settings/constants';
import { setE2EEEnableOnCredentialReady, toggleE2EE } from '../actions';
import { MAX_MODE } from '../constants';
import { canToggleAuthKeyE2EE, doesEveryoneSupportE2EE, isAuthKeyE2EEModerator, isCredentialComplete } from '../functions';
import logger from '../logger';
const useStyles = makeStyles()(() => {
    return {
        e2eeSection: {
            display: 'flex',
            flexDirection: 'column'
        },
        description: {
            fontSize: '0.75rem',
            margin: '15px 0',
            whiteSpace: 'pre-line'
        },
        controlRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '15px',
            '& label': {
                fontSize: '0.875rem',
                fontWeight: 'bold'
            }
        }
    };
});
/**
 * Implements a React {@code Component} for displaying a security dialog section with a field
 * for setting the E2EE key.
 *
 * @param {IProps} props - Component's props.
 * @returns  {JSX}
 */
const E2EESection = ({ _canToggle, _descriptionResource, _enabled, _e2eeLabels, _everyoneSupportE2EE, _toggled, dispatch }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const [toggled, setToggled] = useState(_toggled ?? false);
    useEffect(() => {
        setToggled(_toggled);
    }, [_toggled]);
    /**
     * Callback to be invoked when the user toggles E2EE on or off.
     *
     * @private
     * @returns {void}
     */
    const _onToggle = useCallback((enabled) => {
        const newValue = Boolean(enabled);
        const localState = APP.store.getState();
        if (!canToggleAuthKeyE2EE(localState, newValue)) {
            if (!isAuthKeyE2EEModerator(localState)) {
                logger.error('Only moderator can toggle the e2ee switch');
                dispatch(showMessageNotification({
                    titleKey: 'notify.e2ee',
                    description: 'notify.e2eeOnlyModerator'
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
            else {
                logger.error('Cannot toggle the e2ee switch when there are more than 2 participants');
                dispatch(showWarningNotification({
                    titleKey: 'notify.e2ee',
                    descriptionKey: 'e2ee.enableE2EEWhenOnlyModerator',
                    icon: NOTIFICATION_ICON.WARNING
                }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
            }
            return;
        }
        if (isCredentialComplete() || newValue === false) {
            setToggled(newValue);
            sendAnalytics(createE2EEEvent(`enabled.${String(newValue)}`));
            dispatch(toggleE2EE(newValue));
            // mute everyone while entering the room
            dispatch(setStartMutedPolicy(true, true));
        }
        else {
            dispatch(setE2EEEnableOnCredentialReady(true));
            // open the user profile to complete the credential
            dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
            // show notification for completing user's credential
            dispatch(showWarningNotification({
                titleKey: 'notify.e2ee',
                descriptionKey: 'notify.e2eeCredentialIncomplete',
                icon: NOTIFICATION_ICON.WARNING
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    }, [dispatch]);
    const description = _e2eeLabels?.description || t(_descriptionResource ?? '');
    const label = _e2eeLabels?.label || t('dialog.e2eeLabel');
    const warning = _e2eeLabels?.warning || t('dialog.e2eeWarning');
    return (React.createElement("div", { className: classes.e2eeSection, id: 'e2ee-section' },
        React.createElement("p", { "aria-live": 'polite', className: classes.description, id: 'e2ee-section-description' },
            description,
            !_everyoneSupportE2EE && React.createElement("br", null),
            !_everyoneSupportE2EE && warning),
        React.createElement("div", { className: classes.controlRow },
            React.createElement("label", { htmlFor: 'e2ee-section-switch' }, label),
            React.createElement(Switch, { checked: toggled, disabled: !_enabled || !_canToggle, id: 'e2ee-section-switch', onChange: _onToggle }))));
};
/**
 * Maps (parts of) the Redux state to the associated props for this component.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {IProps}
 */
function mapStateToProps(state) {
    const { enabled: e2eeEnabled, maxMode } = state['features/e2ee'];
    const { e2ee = {} } = state['features/base/config'];
    let descriptionResource = '';
    if (e2ee.labels) {
        // When e2eeLabels are present, the description resource is ignored.
        descriptionResource = undefined;
    }
    else if (maxMode === MAX_MODE.THRESHOLD_EXCEEDED) {
        descriptionResource = 'dialog.e2eeDisabledDueToMaxModeDescription';
    }
    else if (maxMode === MAX_MODE.ENABLED) {
        descriptionResource = e2eeEnabled
            ? 'dialog.e2eeWillDisableDueToMaxModeDescription' : 'dialog.e2eeDisabledDueToMaxModeDescription';
    }
    else {
        descriptionResource = 'dialog.e2eeDescription';
    }
    return {
        _canToggle: isAuthKeyE2EEModerator(state),
        _descriptionResource: descriptionResource,
        _e2eeLabels: e2ee.labels,
        _enabled: maxMode === MAX_MODE.DISABLED || e2eeEnabled,
        _toggled: e2eeEnabled,
        _everyoneSupportE2EE: Boolean(doesEveryoneSupportE2EE(state))
    };
}
export default connect(mapStateToProps)(E2EESection);
